import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { UserCognito, storeUser } from "helpers";
import { Button, CardHeader, ValidationMessage, Input } from "components";
import { confirmSignIn, fetchAuthSession } from "aws-amplify/auth";
import { useAuthContext } from "context";
import { t } from "i18next";
import { useTranslation } from "react-i18next";

interface AuthChangePasswordProps {
  user: UserCognito;
}

interface ChangePasswordFormI {
  newPassword: string;
  confirmPassword: string;
}

const initialData: ChangePasswordFormI = {
  newPassword: "",
  confirmPassword: "",
};

export const AuthChangePassword = ({ user }: AuthChangePasswordProps) => {
  const { setAuth } = useAuthContext();
  const [data, setData] = useState<ChangePasswordFormI>(initialData);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [disabled, setDisabled] = useState(true);

  const onChangeHandler = (event: ChangeEvent<HTMLInputElement>): void => {
    const {
      target: { name, value },
    } = event;
    setData((oldObject) => ({ ...oldObject, [name]: value }));
  };

  const onSubmitHandler = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setDisabled(true);

    if (data.newPassword !== data.confirmPassword) {
      setError("Passwords do not match");
      setDisabled(false);
      return;
    }

    try {
      const { isSignedIn, nextStep } = await confirmSignIn({
        challengeResponse: data.newPassword,
      });

      console.log("Confirm sign-in response:", { isSignedIn, nextStep });

      if (isSignedIn || nextStep.signInStep === "DONE") {
        const session = await fetchAuthSession();
        if (!session.tokens) {
          throw new Error("No authentication tokens available after confirm sign-in");
        }

        const payload = session.tokens.idToken?.payload;
        const accessToken = session.tokens.accessToken;
        const idToken = session.tokens.idToken;

        if (!payload || !accessToken || !idToken) {
          throw new Error("Missing required token data (payload, accessToken, or idToken)");
        }

        const cognitoUsername = payload["cognito:username"];
        const username = typeof cognitoUsername === "string" ? cognitoUsername : user.username;

        const updatedUser: UserCognito = {
          username,
          accessToken: accessToken.toString(),
          idToken: idToken.toString(),
          attributes: {
            sub: payload.sub as string,
            email: payload.email as string,
            phone_number: (payload.phone_number as string) || "",
            email_verified: payload.email_verified === true,
            phone_number_verified: payload.phone_number_verified === true,
          },
          preferredMFA: nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE" ? "SMS_MFA" : "NOMFA",
          challengeName: nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE" ? "SMS_MFA" : undefined,
        };

        storeUser(updatedUser);
        // Check if phone number exists and is unverified
        if (updatedUser.attributes.phone_number && !updatedUser.attributes.phone_number_verified) {
          setAuth("verify-phone-number");
        } else {
          setAuth("success");
        }
      } else if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE") {
        const session = await fetchAuthSession();
        if (!session.tokens) {
          throw new Error("No authentication tokens available for 2FA");
        }

        const payload = session.tokens.idToken?.payload;
        const accessToken = session.tokens.accessToken;
        const idToken = session.tokens.idToken;

        if (!payload || !accessToken || !idToken) {
          throw new Error("Missing required token data for 2FA");
        }

        const cognitoUsername = payload["cognito:username"];
        const username = typeof cognitoUsername === "string" ? cognitoUsername : user.username;

        const updatedUser: UserCognito = {
          username,
          accessToken: accessToken.toString(),
          idToken: idToken.toString(),
          attributes: {
            sub: payload.sub as string,
            email: payload.email as string,
            phone_number: (payload.phone_number as string) || "",
            email_verified: payload.email_verified === true,
            phone_number_verified: payload.phone_number_verified === true,
          },
          preferredMFA: "SMS_MFA",
          challengeName: "SMS_MFA",
        };

        storeUser(updatedUser);
        setAuth("2fa");
      } else {
        throw new Error(`Unhandled sign-in step after confirm: ${nextStep.signInStep}`);
      }
    } catch (err: any) {
      console.error("Confirm sign-in error:", err);
      setError(err.message || "An error occurred while changing the password");
    } finally {
      setDisabled(false);
    }
  };

  useEffect(() => {
    const errors: string[] = [];
    const { newPassword, confirmPassword } = data;

    if (!/(?=.*[a-z])/.test(newPassword)) {
      errors.push(t("validation.passwordNeedsLowercase"));
    }
    if (!/(?=.*[A-Z])/.test(newPassword)) {
      errors.push(t("validation.passwordNeedsUppercase"));
    }
    if (!/(?=.*\d)/.test(newPassword)) {
      errors.push(t("validation.passwordNeedsDigit"));
    }
    if (!/(?=.*[@$!%*?&^#])/.test(newPassword)) {
      errors.push(t("validation.passwordNeedsSpecialChar"));
    }
    if (newPassword.length < 8) {
      errors.push(t("validation.needs8Char"));
    }
    if (confirmPassword.length > 0 && newPassword !== confirmPassword) {
      errors.push(t("validation.passwordDontMatch"));
    }
    if (confirmPassword.length === 0) {
      errors.push(t("validation.passwordConfirmRequired"));
    }

    setValidationErrors(errors);
    setDisabled(errors.length > 0);
    if (error) setError("");
  }, [data, error]);

  return (
    <>
      <CardHeader title={t("auth.setNewPassword")} paragraph={t("auth.setNewPasswordDesc")} />
      <form onSubmit={onSubmitHandler}>
        <div className="space-y-3">
          <Input
            type="password"
            id="newPassword"
            name="newPassword"
            value={data.newPassword}
            placeholder={t("auth.newPassword")}
            onChange={onChangeHandler}
            error={!!error || validationErrors.length > 0}
            autoComplete="new-password"
            className="w-full"
          />
          <Input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={data.confirmPassword}
            placeholder={t("auth.confirmPassword")}
            onChange={onChangeHandler}
            error={!!error || validationErrors.length > 0}
            autoComplete="new-password"
            className="w-full"
          />
        </div>
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          {validationErrors.length > 0 ? (
            validationErrors.map((err, index) => (
              <span key={index} className="inline-flex items-center text-red-500">
                <svg
                  className="mr-1 h-3 w-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                {err}
              </span>
            ))
          ) : data.newPassword.length > 0 && data.confirmPassword.length > 0 ? (
            <span className="inline-flex items-center text-green-500">
              <svg
                className="mr-1 h-3 w-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              {t("auth.allRequirementsMet")}
            </span>
          ) : null}
        </div>
        <ValidationMessage>{error}</ValidationMessage>
        <Button type="submit" className="mx-auto mt-3 px-16 sm:min-w-[200px]" disabled={disabled}>
          {t("common.submit")}
        </Button>
      </form>
    </>
  );
};
